import React, { useState, useRef } from 'react';
import { storeGetOrDefault, storeSet, DEF_PRODUCTS } from '../../store';
import '../admin.css';

const CATEGORIES = ['Zinga', 'Zingaalu', 'Accessories', 'Topcoats', 'Specialty', 'AquaZinga', 'Other'];

const empty = () => ({
  id: '', name: '', category: 'Zinga', price: '', images: [],
  shortDescription: '', description: '', badge: '', stock: 'In Stock',
  specs: [], features: [], applications: []
});

const Toast = ({ msg, type }) => msg ? <div className={`adm-toast adm-toast-${type}`}>{msg}</div> : null;

const ProductsAdmin = () => {
  const [items, setItems]   = useState(() => storeGetOrDefault('products', DEF_PRODUCTS));
  const [mode, setMode]     = useState(null); // null | 'add' | 'edit'
  const [form, setForm]     = useState(empty());
  const [toast, setToast]   = useState({ msg: '', type: 'success' });
  const [search, setSearch] = useState('');
  const [imgUrl, setImgUrl] = useState('');

  const showToast = (msg, type = 'success') => {
    setToast({ msg, type });
    setTimeout(() => setToast({ msg: '', type: 'success' }), 3000);
  };

  const save = (list) => { setItems(list); storeSet('products', list); };

  const openAdd  = () => { setForm(empty()); setImgUrl(''); setMode('add'); };
  const openEdit = (item) => {
    const images = item.images?.length > 0 ? item.images : (item.image ? [item.image] : []);
    setForm({
      ...empty(), ...item, images,
      specs: item.specs || [],
      features: item.features || [],
      applications: item.applications || []
    });
    setImgUrl('');
    setMode('edit');
  };
  const closeModal = () => { setMode(null); setForm(empty()); setImgUrl(''); };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.price.trim()) return showToast('Name and price are required.', 'error');
    const product = { ...form, image: form.images?.[0] || '' };
    if (mode === 'add') {
      const id = form.name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '') + '-' + Date.now();
      save([...items, { ...product, id }]);
      showToast('Product added successfully!');
    } else {
      save(items.map(p => p.id === form.id ? product : p));
      showToast('Product updated!');
    }
    closeModal();
  };

  const handleDelete = (id) => {
    if (!window.confirm('Delete this product? This cannot be undone.')) return;
    save(items.filter(p => p.id !== id));
    showToast('Product deleted.', 'info');
  };

  const move = (idx, dir) => {
    const list = [...items];
    const swap = idx + dir;
    if (swap < 0 || swap >= list.length) return;
    [list[idx], list[swap]] = [list[swap], list[idx]];
    save(list);
  };

  const filtered = items.filter(p =>
    p.name.toLowerCase().includes(search.toLowerCase()) ||
    p.category.toLowerCase().includes(search.toLowerCase())
  );

  const field = (k) => (e) => setForm(f => ({ ...f, [k]: e.target.value }));

  // Specs helpers
  const addSpec       = () => setForm(f => ({ ...f, specs: [...(f.specs||[]), { label: '', value: '' }] }));
  const updateSpec    = (i, k, v) => setForm(f => { const s = [...(f.specs||[])]; s[i] = { ...s[i], [k]: v }; return { ...f, specs: s }; });
  const removeSpec    = (i) => setForm(f => ({ ...f, specs: (f.specs||[]).filter((_,idx) => idx !== i) }));
  // Features helpers
  const addFeature    = () => setForm(f => ({ ...f, features: [...(f.features||[]), ''] }));
  const updateFeature = (i, v) => setForm(f => { const a = [...(f.features||[])]; a[i] = v; return { ...f, features: a }; });
  const removeFeature = (i) => setForm(f => ({ ...f, features: (f.features||[]).filter((_,idx) => idx !== i) }));
  // Applications helpers
  const addApp        = () => setForm(f => ({ ...f, applications: [...(f.applications||[]), ''] }));
  const updateApp     = (i, v) => setForm(f => { const a = [...(f.applications||[])]; a[i] = v; return { ...f, applications: a }; });
  const removeApp     = (i) => setForm(f => ({ ...f, applications: (f.applications||[]).filter((_,idx) => idx !== i) }));

  const imgsRef = useRef(null);
  const handleImgUpload = (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;
    const bad = files.find(f => !f.type.startsWith('image/'));
    if (bad) return showToast('Please select only image files.', 'error');
    const big = files.find(f => f.size > 5 * 1024 * 1024);
    if (big) return showToast('Each image must be under 5 MB.', 'error');
    files.forEach(file => {
      const reader = new FileReader();
      reader.onload = (ev) => setForm(f => ({ ...f, images: [...(f.images || []), ev.target.result] }));
      reader.readAsDataURL(file);
    });
    e.target.value = '';
  };
  const removeImg = (idx) => setForm(f => ({ ...f, images: f.images.filter((_, i) => i !== idx) }));
  const addImgUrl = () => {
    const url = imgUrl.trim();
    if (!url) return;
    if (!/^https?:\/\//.test(url)) return showToast('Enter a valid http/https URL.', 'error');
    setForm(f => ({ ...f, images: [...(f.images || []), url] }));
    setImgUrl('');
  };

  return (
    <div className='adm-page'>
      <Toast {...toast} />

      <div className='adm-header'>
        <div>
          <h1 className='adm-title'>Products</h1>
          <p className='adm-subtitle'>{items.length} products in the catalogue</p>
        </div>
        <button className='adm-btn adm-btn-primary' onClick={openAdd}>+ Add Product</button>
      </div>

      {/* Search */}
      <div style={{ marginBottom: 20 }}>
        <input
          className='adm-input'
          placeholder='Search products by name or category…'
          value={search}
          onChange={e => setSearch(e.target.value)}
          style={{ maxWidth: 400 }}
        />
      </div>

      <div className='adm-table-wrap'>
        <table className='adm-table'>
          <thead>
            <tr>
              <th style={{ width: 40 }}>#</th>
              <th style={{ width: 70 }}>Image</th>
              <th>Name</th>
              <th>Category</th>
              <th>Price (₹)</th>
              <th>Order</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 && (
              <tr><td colSpan={7}><div className='adm-empty'><div className='adm-empty-icon'>📦</div><p>No products found.</p></div></td></tr>
            )}
            {filtered.map((p, idx) => (
              <tr key={p.id}>
                <td style={{ color: '#999', fontSize: '.78rem' }}>{idx + 1}</td>
                <td>
                  {(p.images?.[0] || p.image)
                    ? <img src={p.images?.[0] || p.image} alt={p.name} className='adm-thumb' />
                    : <div style={{ width: 60, height: 44, background: '#f0f0f0', borderRadius: 4, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '.7rem', color: '#999' }}>No img</div>
                  }
                </td>
                <td style={{ fontWeight: 600, maxWidth: 280 }}>{p.name}</td>
                <td><span className='adm-badge adm-badge-blue'>{p.category}</span></td>
                <td>₹ {p.price}</td>
                <td>
                  <div className='adm-order-col'>
                    <button className='adm-order-btn' onClick={() => move(idx, -1)} disabled={idx === 0}>▲</button>
                    <button className='adm-order-btn' onClick={() => move(idx, 1)}  disabled={idx === filtered.length - 1}>▼</button>
                  </div>
                </td>
                <td>
                  <div className='adm-actions'>
                    <button className='adm-btn adm-btn-outline adm-btn-sm' onClick={() => openEdit(p)}>Edit</button>
                    <button className='adm-btn adm-btn-danger  adm-btn-sm' onClick={() => handleDelete(p.id)}>Delete</button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Modal */}
      {mode && (
        <div className='adm-modal-overlay' onClick={(e) => e.target === e.currentTarget && closeModal()}>
          <div className='adm-modal'>
            <div className='adm-modal-header'>
              <h2 className='adm-modal-title'>{mode === 'add' ? 'Add Product' : 'Edit Product'}</h2>
              <button className='adm-close-btn' onClick={closeModal}>✕</button>
            </div>
            <form onSubmit={handleSubmit}>
              <div className='adm-modal-body'>
                <div className='adm-form'>
                  <div className='adm-form-group'>
                    <label className='adm-label adm-label-req'>Product Name</label>
                    <input className='adm-input' value={form.name} onChange={field('name')} placeholder='Full product name' required />
                  </div>
                  <div className='adm-form-row'>
                    <div className='adm-form-group'>
                      <label className='adm-label adm-label-req'>Price (₹)</label>
                      <input className='adm-input' value={form.price} onChange={field('price')} placeholder='2,100.00' required />
                    </div>
                    <div className='adm-form-group'>
                      <label className='adm-label'>Category</label>
                      <select className='adm-input adm-select' value={form.category} onChange={field('category')}>
                        {CATEGORIES.map(c => <option key={c}>{c}</option>)}
                      </select>
                    </div>
                  </div>
                  <div className='adm-form-group'>
                    <label className='adm-label'>Product Images <span style={{fontWeight:400,color:'#9ca3af'}}>(first image = main display)</span></label>
                    {form.images?.length > 0 ? (
                      <div className='adm-img-grid'>
                        {form.images.map((img, i) => (
                          <div key={i} className='adm-img-thumb-wrap'>
                            <img src={img} alt={`img ${i+1}`} className='adm-img-thumb-preview' onError={e => e.target.style.display='none'} />
                            <button type='button' className='adm-img-remove' onClick={() => removeImg(i)}>×</button>
                            {i === 0 && <span className='adm-img-primary-badge'>Main</span>}
                          </div>
                        ))}
                        <div className='adm-img-add-more' onClick={() => imgsRef.current?.click()}>
                          <span>+</span>
                          <small>Add More</small>
                        </div>
                      </div>
                    ) : (
                      <div className='adm-img-upload-zone' onClick={() => imgsRef.current?.click()}>
                        <div className='adm-upload-placeholder'>
                          <div className='icon'>🖼️</div>
                          <span>Click to Upload Images</span>
                          <p>Select multiple — PNG, JPG, WEBP — max 5 MB each</p>
                        </div>
                      </div>
                    )}
                    <input ref={imgsRef} type='file' accept='image/*' multiple style={{ display: 'none' }} onChange={handleImgUpload} />
                    <div className='adm-img-or'>or add image URL</div>
                    <div style={{display:'flex',gap:8}}>
                      <input className='adm-input' value={imgUrl} onChange={e => setImgUrl(e.target.value)} placeholder='https://...' style={{flex:1}} onKeyDown={e => e.key==='Enter' && (e.preventDefault(), addImgUrl())} />
                      <button type='button' className='adm-btn adm-btn-outline' onClick={addImgUrl} style={{whiteSpace:'nowrap'}}>Add URL</button>
                    </div>
                  </div>
                  <div className='adm-form-row'>
                    <div className='adm-form-group'>
                      <label className='adm-label'>Badge <span style={{fontWeight:400,color:'#9ca3af'}}>(e.g. Best Seller)</span></label>
                      <input className='adm-input' value={form.badge||''} onChange={field('badge')} placeholder='Best Seller, Premium, New…' />
                    </div>
                    <div className='adm-form-group'>
                      <label className='adm-label'>Stock Status</label>
                      <select className='adm-input adm-select' value={form.stock||'In Stock'} onChange={field('stock')}>
                        <option>In Stock</option>
                        <option>Out of Stock</option>
                        <option>Pre-Order</option>
                        <option>Contact Us</option>
                      </select>
                    </div>
                  </div>
                  <div className='adm-form-group'>
                    <label className='adm-label'>Short Description</label>
                    <input className='adm-input' value={form.shortDescription||''} onChange={field('shortDescription')} placeholder='One-line product summary shown on product page' />
                  </div>
                  <div className='adm-form-group'>
                    <label className='adm-label'>Full Description</label>
                    <textarea className='adm-input adm-textarea' value={form.description} onChange={field('description')} placeholder='Detailed product description shown in Description tab' />
                  </div>

                  {/* ── Specifications ── */}
                  <div className='adm-form-group'>
                    <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',marginBottom:8}}>
                      <label className='adm-label' style={{margin:0}}>Specifications <span style={{fontWeight:400,color:'#9ca3af'}}>(label / value pairs)</span></label>
                      <button type='button' className='adm-btn adm-btn-outline adm-btn-sm' onClick={addSpec}>+ Add Row</button>
                    </div>
                    {(form.specs||[]).length === 0 && <p style={{fontSize:'.8rem',color:'#9ca3af',margin:0}}>No specifications yet. Click "+ Add Row" to start.</p>}
                    {(form.specs||[]).map((sp, i) => (
                      <div key={i} style={{display:'flex',gap:8,marginBottom:6}}>
                        <input className='adm-input' style={{flex:1}} placeholder='Label (e.g. Zinc Content)' value={sp.label} onChange={e => updateSpec(i,'label',e.target.value)} />
                        <input className='adm-input' style={{flex:1}} placeholder='Value (e.g. 96%)' value={sp.value} onChange={e => updateSpec(i,'value',e.target.value)} />
                        <button type='button' onClick={() => removeSpec(i)} style={{background:'#fee2e2',border:'none',borderRadius:6,padding:'0 10px',cursor:'pointer',color:'#dc2626',fontWeight:700,flexShrink:0}}>×</button>
                      </div>
                    ))}
                  </div>

                  {/* ── Features ── */}
                  <div className='adm-form-group'>
                    <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',marginBottom:8}}>
                      <label className='adm-label' style={{margin:0}}>Features <span style={{fontWeight:400,color:'#9ca3af'}}>(shown in Features tab)</span></label>
                      <button type='button' className='adm-btn adm-btn-outline adm-btn-sm' onClick={addFeature}>+ Add Feature</button>
                    </div>
                    {(form.features||[]).length === 0 && <p style={{fontSize:'.8rem',color:'#9ca3af',margin:0}}>No features yet. Click "+ Add Feature" to start.</p>}
                    {(form.features||[]).map((ft, i) => (
                      <div key={i} style={{display:'flex',gap:8,marginBottom:6}}>
                        <input className='adm-input' style={{flex:1}} placeholder={`Feature ${i+1}`} value={ft} onChange={e => updateFeature(i,e.target.value)} />
                        <button type='button' onClick={() => removeFeature(i)} style={{background:'#fee2e2',border:'none',borderRadius:6,padding:'0 10px',cursor:'pointer',color:'#dc2626',fontWeight:700,flexShrink:0}}>×</button>
                      </div>
                    ))}
                  </div>

                  {/* ── Applications ── */}
                  <div className='adm-form-group'>
                    <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',marginBottom:8}}>
                      <label className='adm-label' style={{margin:0}}>Applications <span style={{fontWeight:400,color:'#9ca3af'}}>(shown in Applications tab)</span></label>
                      <button type='button' className='adm-btn adm-btn-outline adm-btn-sm' onClick={addApp}>+ Add Application</button>
                    </div>
                    {(form.applications||[]).length === 0 && <p style={{fontSize:'.8rem',color:'#9ca3af',margin:0}}>No applications yet. Click "+ Add Application" to start.</p>}
                    {(form.applications||[]).map((ap, i) => (
                      <div key={i} style={{display:'flex',gap:8,marginBottom:6}}>
                        <input className='adm-input' style={{flex:1}} placeholder={`Application ${i+1}`} value={ap} onChange={e => updateApp(i,e.target.value)} />
                        <button type='button' onClick={() => removeApp(i)} style={{background:'#fee2e2',border:'none',borderRadius:6,padding:'0 10px',cursor:'pointer',color:'#dc2626',fontWeight:700,flexShrink:0}}>×</button>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
              <div className='adm-modal-footer'>
                <button type='button' className='adm-btn adm-btn-ghost' onClick={closeModal}>Cancel</button>
                <button type='submit' className='adm-btn adm-btn-primary'>{mode === 'add' ? 'Add Product' : 'Save Changes'}</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProductsAdmin;
